import { EqlFiltersAutoComplete } from '@modules/common/components/EqlFiltersAutoComplete';
import { EqlFiltersAutoCompleteProps } from '@modules/common/components/EqlFiltersAutoComplete/EqlFiltersAutoComplete';
import { QueryFilterActions } from '@modules/queryBuilder/components/QueryFilterActions';
import { QueryFilterConjunctionSelectButton } from '@modules/queryBuilder/components/QueryFilterConjunctionSelectButton';
import { SelectButton } from '@modules/queryBuilder/components/SelectButton';
import {
  getEqlFieldByFieldID,
  getFilterNodeFromSuggestion,
  isFilterValueEmpty,
  removeQueryFilterNode,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { useFetchCustomFilter } from '@modules/queryBuilder/hooks';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { Stack } from '@mui/material';
import { EqlFieldValueType } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { UUID } from 'v2/core/entities/uuid';
import { NormalisedFilter } from 'v2/querybuilder/types/querystore';

interface QueryFilterSelectProps {
  parentID: UUID;
  filter?: NormalisedFilter;
}

export function QueryFilterSelect({
  parentID,
  filter,
}: QueryFilterSelectProps) {
  // ##TODO, Opening the menu by default has side-effects not working well all the cases
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const eqlFieldByFieldID = useQueryBuilderStore(
    ({ metadata }) => metadata.eqlFieldByFieldID,
  );
  const filterIDUnderInspect = useQueryBuilderStore(
    ({ metadata }) => metadata.filterIDUnderInspect,
  );

  const [fetchAndApplyFilters] = useFetchCustomFilter();

  useEffect(() => {
    if (filterIDUnderInspect === filter?.id) {
      setIsOpen(true);
    }
  }, [filterIDUnderInspect, filter?.id]);

  const onFilterSelect: EqlFiltersAutoCompleteProps['onChange'] = async ({
    eqlField,
    eqlFieldValue,
  }) => {
    if (eqlField.valueType === EqlFieldValueType.None) {
      const analysisID = eqlField.detail?.reference?.referenceID;
      await fetchAndApplyFilters(analysisID);
      return;
    }

    const updatedFilter = getFilterNodeFromSuggestion({
      option: { field: eqlField, value: eqlFieldValue, isCompleteMatch: false },
      parent: parentID,
    });

    const filterID = filter?.id || updatedFilter.id;
    const node = { ...updatedFilter, id: filterID };

    const updatedNodes = upsertQueryFilterNode({
      nodesByID: nodes,
      parentID,
      node,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });

    const updatedEqlFieldByFieldID = getEqlFieldByFieldID({
      eqlField,
      eqlFieldByFieldID,
      eqlFieldValue,
    });

    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        filterIDUnderInspect: isFilterValueEmpty(node) ? filterID : null,
        shouldFocusExecuteCTA: !!eqlFieldValue,
        eqlFieldByFieldID: updatedEqlFieldByFieldID,
      },
    });
  };

  const removeFilter = () => {
    if (!filter) {
      return;
    }

    const updatedNodes = removeQueryFilterNode({
      node: { ...filter },
      nodes,
      parentID: parentID,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });
  };

  const clearFilterIDUnderInspect = () => {
    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        filterIDUnderInspect: null,
      },
    });
  };

  const parentNode = parentID && nodes[parentID];
  const isFirstChild = parentNode
    ? parentNode.children?.[0] === filter?.id
    : true;
  const shouldShowConjuction =
    parentNode && parentNode.type === 'group' && !isFirstChild;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={
        filter
          ? {
              [`.filter-actions-${filter.id}`]: {
                visibility: 'hidden',
              },
              '&:hover': {
                [`.filter-actions-${filter.id}`]: {
                  visibility: 'visible',
                },
              },
            }
          : undefined
      }
    >
      {shouldShowConjuction && (
        <QueryFilterConjunctionSelectButton filter={parentNode} />
      )}
      <SelectButton
        shouldShowTooltip={false}
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        label=""
        placeholder="Select a filter to build your query"
        componentsProps={{
          popper: { sx: { width: '60vw' } },
        }}
        renderDropDownComponent={({ open, onClose }) => (
          <EqlFiltersAutoComplete
            isOpen={open}
            onChange={onFilterSelect}
            onClose={() => {
              onClose();
              clearFilterIDUnderInspect();
            }}
          />
        )}
      />
      {filter && (
        <QueryFilterActions
          id={filter.id}
          onRemoveFilter={removeFilter}
          shouldAllowNestedFilter={false}
        />
      )}
    </Stack>
  );
}
